// src/components/Users/Users.tsx
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Autocomplete,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FilterListIcon from "@mui/icons-material/FilterList";
import { baseUrl } from "../../constants";

interface User {
  id: number;
  username: string;
  isActive: boolean;
  branch?: { id: number; name: string };
  imageUrl?: string;
  name: string;
  email: string;
  phone?: string;
  title?: string;
  profile?: {
    name?: string;
    imageUrl?: string;
    email?: string;
    title?: string;
    phone?: string;
  };
}

interface Branch {
  id: number;
  name: string;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBranch, setFilterBranch] = useState<number | null>(null);
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  
  // Dialog states
  const [editBranchDialog, setEditBranchDialog] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const tokenHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (searchQuery) params.name = searchQuery;
      if (filterBranch) params.branchId = filterBranch;
      if (filterActive !== "all") {
        params.isActive = filterActive === "active";
      }
      
      const response = await axios.get(`${baseUrl}/users`, {
        params,
        headers: tokenHeader(),
      });

      // Backend now returns { users, totalCount }
      const userArray = response.data.users || (Array.isArray(response.data) ? response.data : []);
      const total = response.data.totalCount || userArray.length;
      
      setUsers(userArray);
      setTotalUsers(total);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbar({ open: true, message: "Failed to load users", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, filterBranch, filterActive]);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/branches`, {
        headers: tokenHeader(),
      });
      setBranches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleToggleActive = async (user: User) => {
    try {
      await axios.patch(
        `${baseUrl}/users/${user.id}/active`,
        { isActive: !user.isActive },
        { headers: tokenHeader() }
      );
      setSnackbar({ open: true, message: `User ${!user.isActive ? "activated" : "deactivated"}`, severity: "success" });
      fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update user status", severity: "error" });
    }
  };

  const handleEditBranch = async () => {
    if (!editBranchDialog.user) return;
    try {
      await axios.patch(
        `${baseUrl}/users/${editBranchDialog.user.id}/branch`,
        { branchId: selectedBranch?.id || null },
        { headers: tokenHeader() }
      );
      setSnackbar({ open: true, message: "Branch updated successfully", severity: "success" });
      setEditBranchDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update branch", severity: "error" });
    }
  };

  const getInitials = (name?: string, username?: string) => {
    const src = (name || username || "").trim();
    if (!src) return "?";
    const parts = src.split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() ?? "").join("") || src[0]?.toUpperCase() || "?";
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Users Management
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchUsers} variant="outlined">
          Refresh
        </Button>
      </Stack>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" gutterBottom>
              <FilterListIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Filters
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={filterBranch || "all"}
                  onChange={(e) => setFilterBranch(e.target.value === "all" ? null : Number(e.target.value))}
                  displayEmpty
                >
                  <MenuItem value="all">All Branches</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value as any)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Avatar</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      {filterActive !== "all" 
                        ? `No ${filterActive} users found. ${filterActive === "inactive" ? "Try deactivating a user first using the switch." : ""}` 
                        : "No users found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Avatar
                        src={user.profile?.imageUrl || user.imageUrl}
                        alt={user.profile?.name || user.name}
                      >
                        {getInitials(user.profile?.name || user.name, user.username)}
                      </Avatar>
                    </TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.profile?.name || user.name}</TableCell>
                    <TableCell>{user.profile?.email || user.email}</TableCell>
                    <TableCell>
                      {user.branch ? (
                        <Chip icon={<BusinessIcon />} label={user.branch.name} size="small" />
                      ) : (
                        <Typography color="text.secondary" variant="body2">No Branch</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={user.isActive ? "Active" : "Inactive"}
                        color={user.isActive ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Edit Branch">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditBranchDialog({ open: true, user });
                              setSelectedBranch(branches.find(b => b.id === user.branch?.id) || null);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? "Deactivate" : "Activate"}>
                          <Switch
                            checked={user.isActive}
                            onChange={() => handleToggleActive(user)}
                            size="small"
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Edit Branch Dialog */}
      <Dialog open={editBranchDialog.open} onClose={() => setEditBranchDialog({ open: false, user: null })}>
        <DialogTitle>Edit Branch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <Typography variant="body2" color="text.secondary">
              User: {editBranchDialog.user?.username}
            </Typography>
            <Autocomplete
              options={[{ id: 0, name: "No Branch" }, ...branches]}
              getOptionLabel={(option) => option.name}
              value={selectedBranch || { id: 0, name: "No Branch" }}
              onChange={(_, newValue) => setSelectedBranch(newValue?.id === 0 ? null : newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Branch" fullWidth size="small" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBranchDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleEditBranch} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Typography>{snackbar.message}</Typography>
      </Snackbar>
    </Box>
  );
}
